import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import useQuestionnaireStore from '../store/useQuestionnaireStore';
import useWarning from '../hooks/useWarningModal';
import { savePatientReport } from '../services/dbService';
import { ChevronLeft, Mail, User, Phone, Loader } from 'lucide-react';
import Button from '../components/common/Button';

const Step11_Email = () => {
    const navigate = useNavigate();
    const store = useQuestionnaireStore();
    const showWarning = useWarning();
    
    // Local state for form inputs
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        email: store.email || '' // Pre-fill if exists
    });
    
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'phone') {
            // Strictly allow only numbers and max 10 digits
            const numbersOnly = value.replace(/\D/g, '').slice(0, 10);
            setFormData(prev => ({ ...prev, [name]: numbersOnly }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }

        // Sync email to store for consistency
        if (name === 'email') store.setEmail(value);
    };

    const handleNext = async () => {
        const { name, phone, email } = formData;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        
        if (!name.trim()) return showWarning("Please enter your name.");
        if (!phone.trim()) return showWarning("Please enter your phone number.");
        if (!email || !emailRegex.test(email)) return showWarning("Please enter a valid email.");

        setIsSubmitting(true);

        // Prepare full data dump for the "Database" (Google Form)
        const fullReportData = {
            ...formData, // Name, Phone, Email
            // Clinical Data from Store
            painLocation: store.painLocation,
            age: store.age,
            gender: store.gender,
            preciseLocation: store.preciseLocation,
            painDuration: store.painDuration,
            onset: store.onset,
            onsetDetail: store.onsetDetail,
            painIntensity: store.painIntensity,
            painType: store.painType,
            aggravatingFactor: store.aggravatingFactor,
            relievingFactor: store.relievingFactor,
            hasSwelling: store.hasSwelling,
            worsensWithPressure: store.worsensWithPressure,
            medicalConditions: store.medicalConditions
        };

        try {
            await savePatientReport(fullReportData);
            console.log("Report submitted to form");
            navigate('/patient-report');
        } catch (error) {
            console.error("Submission error", error);
            showWarning("Something went wrong. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <motion.div
            className="page step11-page"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
        >
            <div className="step-header">
                <button onClick={() => navigate(-1)} className="back-button">
                    <ChevronLeft size={24} /> Back
                </button>
                <h2>Final Step</h2>
                <p>Enter your details to receive your recovery plan.</p>
            </div>

            <div className="email-container">
                
                {/* Name Input */}
                <div className="input-group">
                    <label>Full Name</label>
                    <div className="input-wrapper">
                        <User className="input-icon" size={20} />
                        <input 
                            name="name"
                            type="text" 
                            placeholder="John Doe" 
                            value={formData.name}
                            onChange={handleChange}
                            className="form-input"
                        />
                    </div>
                </div>

                {/* Phone Input */}
                <div className="input-group">
                    <label>Phone Number</label>
                    <div className="input-wrapper">
                        <Phone className="input-icon" size={20} />
                        <input 
                            name="phone"
                            type="tel" 
                            placeholder="+91 98765 43210" 
                            value={formData.phone}
                            onChange={handleChange}
                            className="form-input"
                            maxLength={10}
                        />
                    </div>
                </div>

                {/* Email Input */}
                <div className="input-group">
                    <label>Email Address</label>
                    <div className="input-wrapper">
                        <Mail className="input-icon" size={20} />
                        <input 
                            name="email"
                            type="email" 
                            placeholder="john@example.com" 
                            value={formData.email}
                            onChange={handleChange}
                            className="form-input"
                        />
                    </div>
                </div>

                <p className="privacy-note">
                    Your assessment results will be generated instantly.
                </p>
            </div>

            <div className="navigation-buttons">
                <Button onClick={handleNext} disabled={isSubmitting}>
                    {isSubmitting ? (
                        <><Loader className="spin" size={18} style={{marginRight:8}}/> Generating Report...</>
                    ) : (
                        "See My Results"
                    )}
                </Button>
            </div>
        </motion.div>
    );
};

export default Step11_Email;